import { supabase } from '../lib/supabase';

export class StorageService {
  static COMPANY_IMAGES_BUCKET = 'company-images';

  // Upload a file to Supabase Storage
  static async uploadFile(
    bucket: string,
    path: string,
    file: File
  ): Promise<{ data: any; error: any }> {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: true, // Overwrite if file exists
        });

      return { data, error };
    } catch (error) {
      console.error('Error uploading file:', error);
      return { data: null, error };
    }
  }

  // Get public URL for a file
  static getPublicUrl(bucket: string, path: string): string {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(path);

    return data.publicUrl;
  }

  // Delete a file from storage
  static async deleteFile(bucket: string, path: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      return { error };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { error };
    }
  }

  // Upload category image
  static async uploadCategoryImage(categoryId: string, file: File): Promise<string | null> {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${categoryId}.${fileExt}`;
      const filePath = `categories/${fileName}`;

      // Upload file
      const { data, error } = await this.uploadFile('category-images', filePath, file);
      
      if (error) {
        console.error('Error uploading category image:', error);
        return null;
      }

      // Return public URL
      return this.getPublicUrl('category-images', filePath);
    } catch (error) {
      console.error('Error uploading category image:', error);
      return null;
    }
  }

  // Delete category image
  static async deleteCategoryImage(imageUrl: string): Promise<boolean> {
    try {
      // Extract path from URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.findIndex(part => part === 'category-images');
      
      if (bucketIndex === -1) {
        console.error('Invalid category image URL');
        return false;
      }

      const path = pathParts.slice(bucketIndex + 1).join('/');
      const { error } = await this.deleteFile('category-images', path);
      
      if (error) {
        console.error('Error deleting category image:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting category image:', error);
      return false;
    }
  }

  // Upload subcategory image
  static async uploadSubcategoryImage(subcategoryId: string, file: File): Promise<string | null> {
    try {
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${subcategoryId}.${fileExt}`;
      const filePath = `subcategories/${fileName}`;

      // Upload file
      const { data, error } = await this.uploadFile('subcategory-images', filePath, file);
      
      if (error) {
        console.error('Error uploading subcategory image:', error);
        return null;
      }

      // Return public URL
      return this.getPublicUrl('subcategory-images', filePath);
    } catch (error) {
      console.error('Error uploading subcategory image:', error);
      return null;
    }
  }

  // Delete subcategory image
  static async deleteSubcategoryImage(imageUrl: string): Promise<boolean> {
    try {
      // Extract path from URL
      const url = new URL(imageUrl);
      const pathParts = url.pathname.split('/');
      const bucketIndex = pathParts.findIndex(part => part === 'subcategory-images');
      
      if (bucketIndex === -1) {
        console.error('Invalid subcategory image URL');
        return false;
      }

      const path = pathParts.slice(bucketIndex + 1).join('/');
      const { error } = await this.deleteFile('subcategory-images', path);
      
      if (error) {
        console.error('Error deleting subcategory image:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting subcategory image:', error);
      return false;
    }
  }

  // Check if URL is from our storage bucket
  static isStorageUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('supabase.co') && 
             (urlObj.pathname.includes('category-images') || urlObj.pathname.includes('subcategory-images'));
    } catch {
      return false;
    }
  }

  // Check if URL is from category storage
  static isCategoryStorageUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('supabase.co') && 
             urlObj.pathname.includes('category-images');
    } catch {
      return false;
    }
  }

  // Check if URL is from subcategory storage
  static isSubcategoryStorageUrl(url: string): boolean {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('supabase.co') && 
             urlObj.pathname.includes('subcategory-images');
    } catch {
      return false;
    }
  }

  static async uploadCompanyLogo(companyId: string, file: File): Promise<string> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in to upload images.');
    }

    const fileExt = file.name.split('.').pop() || 'png';
    const filePath = `${user.id}/companies/${companyId}/logo.${fileExt}`;
    const { error } = await this.uploadFile(this.COMPANY_IMAGES_BUCKET, filePath, file);
    if (error) throw error;
    return this.getPublicUrl(this.COMPANY_IMAGES_BUCKET, filePath);
  }

  static async uploadCompanyCover(companyId: string, file: File): Promise<string> {
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      throw new Error('You must be logged in to upload images.');
    }

    const fileExt = file.name.split('.').pop() || 'jpg';
    const filePath = `${user.id}/companies/${companyId}/cover.${fileExt}`;
    const { error } = await this.uploadFile(this.COMPANY_IMAGES_BUCKET, filePath, file);
    if (error) throw error;
    return this.getPublicUrl(this.COMPANY_IMAGES_BUCKET, filePath);
  }
}
